<template>
  <div class="form">
    <h1 class="form__title">{{ t('auth.register.title') }}</h1>
    <p class="form__text">{{ t('auth.register.login') }} <router-link :to="translations.i18nRoute({ name: 'auth-login' })">{{ t('buttons.signIn') }}</router-link></p>
    <form @submit.prevent="register" class="form__form">
      <div class="form__box">
        <ui-input
            :type="'text'"
            :placeholder="t('fields.firstName')"
            :rules="[required]"
            v-model="firstName"
        />
        <ui-input
            :type="'text'"
            :placeholder="t('fields.lastName')"
            :rules="[required]"
            v-model="lastName"
        />
      </div>
      <ui-input
          :type="'email'"
          :placeholder="t('fields.email')"
          :rules="[isEmail]"
          v-model="email"
      />
      <ui-input
          :type="'password'"
          :placeholder="t('fields.password')"
          :rules="[isPasswordValid]"
          v-model="password"
      />
      <ui-input
          :type="'password'"
          :placeholder="t('fields.confirmPassword')"
          :rules="[compareStrings]"
          v-model="confirmPassword"
      />
      <div class="form__checkboxes">
        <ui-checkbox
            v-model="isAgreePolicy"
            :id="1"
        >
          <p>
            {{ t('checkboxes.accept') }}
            <router-link :to="translations.i18nRoute({ name: 'home' })" class="form__link">
              {{ t('docs.terms.title') }}
            </router-link>
          </p>
        </ui-checkbox>
        <ui-checkbox
            v-model="isAgreeTerms"
            :id="2"
        >
          <p>
            {{ t('checkboxes.accept') }}
            <router-link :to="translations.i18nRoute({ name: 'policy' })" class="form__link">
              {{ t('docs.policy.title') }}
            </router-link>
          </p>
        </ui-checkbox>
      </div>
      <ui-button
          :type="'submit'"
          class="form__button"
          :isDisabled="!isFormValid"
          :isLoading="isLoading"
      >
        {{ t('buttons.signUp') }}
      </ui-button>
    </form>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import translations from "@/core/helpers/translations.js";
import {isEmail, isPasswordValid, required} from "@/core/rules/textFieldRules.js";
import UiInput from "@/components/ui/ui-input.vue";
import {computed, h, ref} from "vue";
import UiCheckbox from "@/components/ui/ui-checkbox.vue";
import UiButton from "@/components/ui/ui-button.vue";
import {ElNotification} from "element-plus";
import {Auth} from "@/core/api/auth.js";

const {t} = useI18n()

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const isAgreePolicy = ref(false)
const isAgreeTerms = ref(false)

const isLoading = ref(false)
const mailClient = ref(null)

const compareStrings = (v) => {
  if (v === password.value) return true
  return t('errors.compare')
}

const isFormValid = computed(() => {
  return (
      required(firstName.value) === true &&
      required(lastName.value) === true &&
      isEmail(email.value) === true &&
      isPasswordValid(password.value) === true &&
      compareStrings(confirmPassword.value) === true &&
      isAgreePolicy.value === true &&
      isAgreeTerms.value === true
  )
})

const register = async () => {
  isLoading.value = true

  const res = await Auth.register({
    firstName: firstName.value,
    lastName: lastName.value,
    email: email.value,
    password: password.value,
    passwordConfirm: confirmPassword.value,
  })

  if (!res.errors) {
    const domain = email.value.split('@')[1]
    const mailClients = {
      'gmail.com': 'https://mail.google.com/',
      'outlook.com': 'https://outlook.live.com/',
      'icloud.com': 'https://www.icloud.com/',
      'yahoo.com': 'https://mail.yahoo.com/'
    }

    Object.entries(mailClients).forEach((el) => {
      if (domain === el[0]) mailClient.value = el[1]
    })

    ElNotification({
      message: h('div', [
        h('p', t('popup.successRegister')),
        mailClient ? h(
            'button',
            {
              style: {
                marginTop: '10px',
                padding: '6px 12px',
                backgroundColor: '#ff0000',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              },
              onClick: () => {
                window.location.href = mailClient.value
              }
            },
            t('buttons.goEmail')
        ) : ''
      ]),
      type: 'success'
    })
  } else {
    ElNotification({
      message: res.errors[0].message,
      type: 'error'
    })
  }

  isLoading.value = false
}
</script>

<style lang="scss" scoped>
.form {
  &__title {
    color: #F3F3F3;
    text-align: center;
    font-size: 64px;
    font-weight: 700;
  }

  &__text {
    margin-top: 15px;
    color: $white;
    text-align: center;
    font-size: 20px;

    & a {
      transition: 0.2s;
      font-weight: 700;

      @include hover {
        color: #b7b7b7;
      }
    }
  }

  &__form {
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }

  &__box {
    display: flex;
    align-items: flex-start;
    gap: 15px;
  }

  &__checkboxes {
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 35px;
  }

  &__link {
    text-decoration: underline;
  }

  &__button {
    margin-top: 10px;
    width: fit-content !important;
    padding: 8px 40px;
  }
}

@media (max-width: 600px) {
  .form {
    &__box {
      width: 100%;
      flex-direction: column;
    }

    &__checkboxes {
      flex-direction: column;
      gap: 15px;
    }
  }
}
</style>